/**
 * The hierarchy view (`KLQ-20260824-001`) — the Tasks tab's third arrangement, and the one
 * `chat1.md` line 221 reserved from the start: *"List/board/hierarchy views"*.
 *
 * It answers a question neither of the other two can: **what is planned where, and what will
 * come out of it.** Locations, the work filed at each, and beneath each task the drawings it
 * owns, with the identifier, the current version and the derived status the spec names.
 *
 * FOUR THINGS THIS FILE IS BUILT ON
 *
 * 1. **The arrangement is a mode of one shared table**, not a component of its own. Rows come
 *    from `plan.ts` and are drawn by `components/tree-table`, which also renders them flat —
 *    the same rows, one switch. The primitive knows nothing about tasks, because the drawings
 *    tree and the planliste register are the next two things that need it.
 * 2. **The rail is not here.** It was, while this was the only view that had one; on
 *    2026-08-25 it moved up to `TasksPage`, above all three arrangements, and the filter bar's
 *    Location `<select>` was deleted rather than left as a second control writing the same
 *    `under=`. What reaches this file is the filter object, already sliced. Nothing here reads
 *    a rail and nothing here draws one.
 * 3. **The plan is grouped from the pages this screen holds**, and says so. `KLQ-20260821-001`
 *    §A declined to group the register for exactly this reason: a group inside a paginated
 *    list is complete only when every one of its rows is loaded. The register's answer was to
 *    stay flat; this view's answer is the honest one available to it — every count carries
 *    §D's `+` while the cursor has more to give, and *Show older* is offered until it does
 *    not. A group header here means *this much of that floor's work is loaded*, never *this
 *    is that floor's work*.
 * 4. **The tree costs no request of its own.** The hierarchy, the slot list and the current
 *    versions are C1a's three cached keys, imported rather than re-declared, so opening this
 *    view after the Drawings tab costs nothing and the two screens cannot disagree about where
 *    a drawing sits (`~/.claude/CLAUDE.md`, data fetching).
 *
 * Nothing here decides who may see what: the tree and the slots arrive pruned to the caller's
 * zones and the task pages cut to their audience, and a second filter on this side would be a
 * second implementation of a rule that has one (retro rule 6).
 */

import { useMemo, useState } from "react";

import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Button,
  EditingProvider,
  EmptyState,
  InlineError,
  Pill,
  Select,
  SelectionProvider,
  SkeletonRows,
  TreeTable,
  UndoButtons,
  useSelection,
} from "@/components";
import type { PillTone } from "@/components";
import { SelectionBar } from "@/components/SelectionBar";
import type { MenuItem } from "@/components/MenuPopup";
import type { EditBatch, EditResult } from "@/components";
import type { TreeTableColumn, TreeTableMode } from "@/components";
import {
  buildTree,
  currentVersionsQueryOptions,
  hierarchyQueryOptions,
  responsibleByNode,
  responsibleByObject,
  setNodeResponsible,
  slotsQueryOptions,
} from "@/features/drawings";
import type { DeliverableStatus, ResolvedResponsible } from "@/features/drawings";
import { membersQueryOptions } from "@/features/people";
import {
  objectBucketId,
  objectPivotGroups,
  phaseLabel,
  relativeTime,
  useProjectObjects,
  useProjectPhases,
} from "@/features/projects";
import { leafIdOf } from "@/lib/pivot";
import type { MessageKeys, Translate } from "@/lib/i18n";
import { useLocale, useMessages } from "@/lib/i18n";

import { TASK_PRIORITIES, TASK_STATUSES } from "./api";
import type {
  Task,
  TaskBulkEdit,
  TaskPriority,
  TaskStatus,
} from "./api";
import type { TaskFilters } from "./filters";
import { isNarrowed, NO_FILTERS } from "./filters";
import { KIND_KEY } from "./kinds";
import { LoadedCount } from "./LoadedCount";
import { SliceBar } from "./SliceBar";
import { useBranchSentinels, useInfiniteSentinel } from "@/lib/infinite";
import { useTaskImportAdapter } from "./importTasks";
import {
  buildPlan,
  inheritedBeneath,
  pivotPlan,
  planSubjects,
  UNPLACED_ID,
} from "./plan";
import type { PlanAxis } from "./plan";
import type { PlanNode, PlanRow } from "./plan";
import { taskMenuItems } from "./actions";
import type { TaskBulkIntent } from "./actions";
import { bulkSkipKey, detailOf, taskErrorKey } from "./problems";
import {
  renderPersonName,
  renderPersonNames,
  useBulkEditTasks,
  useMemberNames,
  useNodeNames,
  useProjectLabels,
  useBranchTasks,
  useTaskCounts,
  useTaskList,
  useTasksInvalidation,
} from "./queries";
import { KindGlyph } from "./TaskRegister";
// UX cleanup 2026-08-25 — `Create task` is drawn once, by `TasksPage`, for every view.
// import { TaskCreateDrawer } from "./TaskCreateDrawer";
import { useTaskFilterNavigation } from "./TaskFilterBar";
import styles from "./Tasks.module.css";

type Key = MessageKeys["tasks"];

const STATUS_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: "status.open",
  in_progress: "status.inProgress",
  done: "status.done",
  closed: "status.closed",
};

/** TWIN: the same table in `TaskFilterBar` and `TaskDetail` — one word per priority. */
const PRIORITY_KEY: Readonly<Record<TaskPriority, Key>> = {
  high: "priority.high",
  normal: "priority.normal",
  low: "priority.low",
};

/** TWIN: `STATUS_TONE` in `TaskRegister.tsx` — the same four states in a second arrangement. */
const STATUS_TONE: Readonly<Record<TaskStatus, PillTone>> = {
  open: "info",
  in_progress: "warning",
  done: "success",
  closed: "neutral",
};

/** The derived status of a deliverable. Same ramp as the task pills — see `TaskDetail`. */
const DELIVERABLE_KEY: Readonly<Record<DeliverableStatus, Key>> = {
  planned: "deliverable.status.planned",
  in_progress: "deliverable.status.inProgress",
  done: "deliverable.status.done",
};

const DELIVERABLE_TONE: Readonly<Record<DeliverableStatus, PillTone>> = {
  planned: "info",
  in_progress: "warning",
  done: "success",
};

export interface TaskHierarchyProps {
  readonly projectId: string;
  readonly filters: TaskFilters;
}

export function TaskHierarchy({ projectId, filters }: TaskHierarchyProps) {
  const t = useMessages("tasks");
  const { locale } = useLocale();
  // The view is named for invalidation's sake; the cache entry it resolves to is the
  // register's, because this view issues the register's request verbatim and groups the answer
  // here (`queries.ts`, `shapeOf`). Toggling register<->plan therefore costs no request.
  /**
   * Which levels are showing their children — **reported by the table, not decided here.**
   *
   * Which branches are open is `TreeTable`'s state and its default is derived from the nodes,
   * so reproducing that arithmetic here would be a second opinion that goes stale the first
   * time the table's default changes. `useState`'s setter is stable, which the prop requires.
   */
  const [openLevels, setOpenLevels] = useState<readonly string[]>([]);
  const hierarchy = useQuery(hierarchyQueryOptions(projectId));
  const slots = useQuery(slotsQueryOptions(projectId));
  const currents = useQuery(currentVersionsQueryOptions(projectId));
  const nameOf = useMemberNames(projectId);
  const nodeNameOf = useNodeNames(projectId);
  const apply = useTaskFilterNavigation(projectId, "hierarchy");
  // Watched by the observer that asks for the next page (`lib/infinite.ts`). State, not a ref:
  // a ref's `.current` changing does not re-render, so the effect would never see it arrive.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  /**
   * **Grouped by what, and *whether*** — one control since `KLQ-20260910-001` §E.
   *
   * There used to be two: a `ViewToggle` holding *Grouped / Flat*, and a group-by `<Select>`
   * rendered only while it said *Grouped*. That is one question asked twice — there is no such
   * thing as grouped-by-nothing and no such thing as flat-with-an-axis — and it cost a person
   * two controls to express one intention. `null` is the flat arrangement; anything else is
   * both the decision to group and the axis to group on, and `mode` is derived rather than
   * held, so the table's own `"flat" | "grouped"` prop is untouched.
   *
   * Component state, not a URL parameter: two people opening one shared link must see the same
   * rows, and how one of them has arranged those rows is not part of the question the link
   * asks (`KLQ-20260820-006` §0.4).
   */
  const [axis, setAxis] = useState<PlanAxis | null>("location");
  const mode: TreeTableMode = axis === null ? "flat" : "grouped";

  /**
   * **Contents per branch, unless something needs the whole set** (`KLQ-20260825-008` §5.1;
   * owner 2026-09-10: *"if they are in a hierarchy then fetch when the node is opened"*).
   *
   * A pivot re-parents every row by an attribute — object, phase, status, priority — so it has
   * to see rows the location tree has not opened. There is no honest way to pivot a partial
   * set: a phase column built from four open floors is not *"tasks in DSP"*, it is *"the DSP
   * tasks of four floors"*, which reads the same and is not. So the pivot fetches the register
   * whole and says so here rather than quietly showing less.
   *
   * The location arrangement — the default, and the one people live in — fetches a level's work
   * when the level is opened, and nothing else.
   */
  const wholeProject = axis !== null && axis !== "location";
  const list = useTaskList(projectId, filters, "hierarchy", wholeProject);
  const branches = useBranchTasks(
    projectId,
    filters,
    wholeProject ? [] : openLevels,
  );
  // One observer over every branch's sentinel row — see `useBranchSentinels`. `branches.reach`
  // is a `useCallback`, which is what the hook requires of it.
  const sentinelFor = useBranchSentinels(branches.reach);
  // const [creating, setCreating] = useState(false);

  // Memoised, because the plan is built from it: a fresh array identity on every render would
  // rebuild the whole tree for nothing.
  const tasks = useMemo(
    () =>
      wholeProject
        ? (list.data?.pages.flatMap((page) => page.items) ?? [])
        : branches.tasks,
    [wholeProject, list.data, branches.tasks],
  );
  // Only the whole-project fetch has a next page. A plan drawn per branch does not run out of
  // rows — it runs out of *open branches*, and the thing that loads more is the twisty.
  const hasMore = wholeProject && (list.data?.pages.at(-1)?.has_more ?? false);
  // *"Counts cover what is loaded."* True only while the **register** is still paging. A
  // branch that has more no longer needs the sentence: it draws its own row at the end of
  // itself, which says the same thing where the missing rows would be.
  const partial = hasMore;
  // What the slice leaves out, off the page this view already holds — the register's number,
  // from the register's cache entry, because the plan issues that request verbatim.
  // The register's number, from the register's cache entry — so it exists only while the
  // register's request is the one being made. A per-branch fetch names a depth, and the server
  // answers `null` for it deliberately: one level of a tree is not hiding part of a project.
  const excludedBySlice = wholeProject
    ? (list.data?.pages.at(-1)?.excluded_by_slice ?? null)
    : null;

  useInfiniteSentinel(sentinel, {
    hasNextPage: wholeProject && list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: () => {
      void list.fetchNextPage();
    },
  });

  const tree = useMemo(
    () =>
      hierarchy.data === undefined ||
      slots.data === undefined ||
      currents.data === undefined
        ? null
        : buildTree(
            hierarchy.data.nodes,
            slots.data.items,
            currents.data.items,
          ),
    [hierarchy.data, slots.data, currents.data],
  );

  // The composition, for the object half of §C.3. Unpaginated by contract — a table of
  // contents, tens of rows — and cached with everything else that describes the project rather
  // than fetched per row.
  const objects = useProjectObjects(projectId);
  const queryClient = useQueryClient();

  /**
   * Who is responsible, resolved once per data change (`KLQ-20260828-003`).
   *
   * Two maps rather than a field on every row: the walk is one pass over each tree, and
   * rebuilding the plan for an unrelated reason must not re-walk them.
   */
  const byNode = useMemo(
    () => (tree === null ? new Map() : responsibleByNode(tree.roots)),
    [tree],
  );
  const byObject = useMemo(
    () => responsibleByObject(objects.data ?? []),
    [objects.data],
  );

  /**
   * How much work each level holds, from the server (`KLQ-20260825-008` §5.1).
   *
   * **It answers the register's question, not the project's**, which is why it takes the same
   * `filters` object the list does: a count that ignored `status=open` would put *"12"* above a
   * branch that opens to show three, and `KLQ-20260820-005` calls that *"a lie stated in
   * numerals"*. One request per filter change, cached beside the list and invalidated with it.
   *
   * The rail's `under=` is deliberately **not** passed. The plan draws the slice it was handed
   * and the counts are read per node id, so narrowing the count query would only make a level
   * outside the slice report nothing — a level that is not drawn anyway.
   */
  const counts = useTaskCounts(projectId, filters);

  /**
   * The server's numbers as one map, with the unplaced bucket folded in under its own row id.
   *
   * `undefined` until the answer arrives, so `buildPlan` keeps counting what it has for one
   * render rather than drawing an empty plan — the old behaviour is the loading state.
   */
  const countsByNode = useMemo<ReadonlyMap<string, number> | undefined>(() => {
    if (counts.data === undefined) return undefined;
    const map = new Map<string, number>(Object.entries(counts.data.by_node));
    map.set(UNPLACED_ID, counts.data.unplaced);
    return map;
  }, [counts.data]);

  const plan = useMemo(
    () =>
      tree === null
        ? []
        : buildPlan(
            tree.roots,
            tree.slots,
            tasks,
            byNode,
            byObject,
            countsByNode,
            branches.more,
          ),
    [tree, tasks, byNode, byObject, countsByNode, branches.more],
  );

  const phases = useProjectPhases(projectId);

  /**
   * The plan as it is **drawn** — `plan` regrouped, or `plan` itself.
   *
   * Everything downstream reads this rather than `plan`: `planSubjects` indexes whatever tree
   * it is handed, so the selection, the menu and the action bar need no notion of an axis at
   * all. The one place that does is `write`, which turns a row id into a task id and therefore
   * unwraps through `leafIdOf`.
   */
  /**
   * `node id → the object it is filed under`, for drawings only.
   *
   * Built from the slot list this view already holds (`tree.slots`), so the task pivot costs no
   * request to learn where a task's own sheet sits. A container is absent from the map, which
   * is correct: a floor is not filed under an object.
   */
  const objectOfNode = useMemo(
    () => new Map((tree?.slots ?? []).map((slot) => [slot.id, slot.objectId])),
    [tree],
  );

  const drawn = useMemo(() => {
    // `null` is the flat arrangement, and the location axis is the tree as it already stands:
    // neither is a pivot, so both hand `plan` straight back. `TreeTable` renders the same tree
    // flat when its `mode` says so, which is why flatness costs nothing here.
    if (axis === null || axis === "location") return plan;
    const objectRows = objects.data ?? [];
    const phaseRows = phases.data ?? [];
    switch (axis) {
      case "object":
        return pivotPlan(plan, {
          groups: objectPivotGroups(objectRows),
          /**
           * A task has no object of its own. It reaches one **two ways**, and both count
           * (owner, 2026-09-16):
           *
           * 1. its **deliverables** are filed there — one task legitimately produces drawings
           *    under two objects, and several keys means several rows;
           * 2. it **sits on** a drawing filed there — an issue raised on a sheet in SO 101 is
           *    in SO 101, and leaving that out is what made a project's tasks look unfiled
           *    after its documents had been filed.
           *
           * TWIN: `_produces_for_any` in `domains/tasks/service.py`, which is this sentence as
           * SQL. The filter and the pivot have to agree about what *"this task is in SO 101"*
           * means; two rules that merely resemble each other disagree on screen eventually.
           */
          keysOf: (task, deliverables) => {
            const keys = deliverables.flatMap((slot) =>
              slot.objectId === null ? [] : [objectBucketId(slot.objectId)],
            );
            const own =
              task.hierarchy_node_id === null
                ? null
                : (objectOfNode.get(task.hierarchy_node_id) ?? null);
            if (own !== null) keys.push(objectBucketId(own));
            // One row per object, not one per deliverable that happens to name the same one.
            return [...new Set(keys)];
          },
          keeps: (bucketId, slot) =>
            slot.objectId !== null && objectBucketId(slot.objectId) === bucketId,
          unassignedLabel: t("axis.noObject"),
        });
      case "phase":
        return pivotPlan(plan, {
          groups: phaseRows.map((phase) => ({
            id: `phase:${phase.id}`,
            label: phaseLabel(phase),
            parentId: null,
          })),
          keysOf: (_task, deliverables) =>
            deliverables.flatMap((slot) =>
              slot.phaseId === null ? [] : [`phase:${slot.phaseId}`],
            ),
          keeps: (bucketId, slot) =>
            slot.phaseId !== null && `phase:${slot.phaseId}` === bucketId,
          unassignedLabel: t("axis.noPhase"),
        });
      case "status":
        return pivotPlan(plan, {
          // The catalogue order, not the data's: `TASK_STATUSES` is the order this product
          // draws a lifecycle in, and the board already spends it.
          groups: TASK_STATUSES.map((status) => ({
            id: `status:${status}`,
            label: t(STATUS_KEY[status]),
            parentId: null,
          })),
          keysOf: (task) => [`status:${task.status}`],
          unassignedLabel: t("axis.noStatus"),
        });
      case "priority":
        return pivotPlan(plan, {
          groups: TASK_PRIORITIES.map((priority) => ({
            id: `priority:${priority}`,
            label: t(PRIORITY_KEY[priority]),
            parentId: null,
          })),
          keysOf: (task) => [`priority:${task.priority}`],
          unassignedLabel: t("axis.noPriority"),
        });
    }
  }, [axis, plan, objects.data, phases.data, objectOfNode, t]);

  /**
   * Which drawn rows are levels — the one fact `write` needs to know where a value goes.
   *
   * A set rather than a walk per write: `write` runs inside a gesture, and a fan-out over three
   * hundred rows would otherwise walk the plan three hundred times.
   */
  const containerIds = useMemo(() => {
    const ids = new Set<string>();
    const walk = (items: readonly PlanNode[]): void => {
      for (const node of items) {
        if (node.row.kind === "container") ids.add(node.id);
        walk(node.children);
      }
    };
    walk(drawn);
    return ids;
  }, [drawn]);

  const bulkEdit = useBulkEditTasks(projectId);
  // What a pasted sheet means here (`KLQ-20260824-002`). Declared on this view and not on the
  // primitive: the mechanism owns the preview, the mapping and the memory; only this file
  // knows that a row is a task and where it hangs.
  const importAdapter = useTaskImportAdapter(projectId);
  const refetchTasks = useTasksInvalidation(projectId);
  // Who this project's work may be given to. One cached list, shared with the create drawer
  // and the filter bar rather than fetched again per editable cell.
  const members = useQuery(membersQueryOptions(projectId));
  const memberOptions = useMemo(
    () =>
      (members.data?.items ?? []).map((row) => ({
        id: row.user_id,
        label: renderPersonName(t, nameOf(row.user_id), t("row.unassigned")),
      })),
    [members.data, nameOf, t],
  );
  // The delta the writer sends is computed from what each task holds now.
  const assigneesOf = useMemo(
    () =>
      new Map<string, readonly string[]>(
        tasks.map((task) => [task.id, task.assignee_user_ids]),
      ),
    [tasks],
  );

  const columns: readonly TreeTableColumn<PlanRow>[] = useMemo(() => {
    /**
     * A resolved responsible, as text — the clipboard's answer and the "did anything change?"
     * comparison's (`KLQ-20260828-001` §A.3). Empty when nobody owns the row, which is the
     * honest thing for a sheet: a column of *"unassigned"* is noise a studio has to delete.
     */
    const personLabel = (who: ResolvedResponsible | null): string =>
      who === null ? "" : renderPersonName(t, nameOf(who.userId), "");

    /**
     * The same value, drawn — and drawn *differently* when it was inherited (§E).
     *
     * Quieter ink and the source in the title, because the one thing an inherited name owes the
     * reader is that it was not chosen for this row. Without that, a person reading a floor's
     * worth of tasks would believe five people had each been named individually.
     */
    const inheritedCell = (who: ResolvedResponsible | null) => {
      if (who === null) return null;
      const name = personLabel(who);
      if (who.from === null) return <span className={styles.rowMeta}>{name}</span>;
      return (
        <span
          className={styles.inherited}
          title={t("plan.responsibleFrom", { name: who.from.name })}
        >
          {name}
        </span>
      );
    };

    return [
      {
        key: "name",
        header: t("plan.column.what"),
        width: "auto",
        // The card's first line on a phone (`KLQ-20260905-002` §C): what the row *is*.
        mobile: "title",
        cell: (row) => (
          <NameCell
            projectId={projectId}
            row={row}
            t={t}
            hasMore={hasMore}
            sentinelFor={sentinelFor}
          />
        ),
        // `value` is what this cell *is*, as text (`KLQ-20260828-001` §A.3): one definition
        // for the clipboard, and later for the editor's starting point and the did-anything-
        // change check. Never an id — a studio pasting this into a sheet wants the name they
        // use for the thing, which for a deliverable is its identifier.
        value: (row) => rowName(row, t),
        /**
         * Where the copied cell leads (owner, 2026-09-10). The note that asked for this named
         * a level — *"there is only 4.NP and no link"* — so a level links too, not only the
         * things standing on it.
         *
         * A task goes to its detail; a deliverable goes to the drawing's viewer, because a
         * drawing on the plan is still a drawing and its address is the viewer's. A real level
         * goes to this register re-rooted under it. The two rows with no address get none: a
         * synthetic pivot heading has no row behind it, and *Unplaced* is a bucket rather than
         * a level — `?under=` cannot say "nowhere".
         */
        href: (row) => {
          if (row.kind === "task") {
            return `/projects/${projectId}/tasks/${row.task.id}`;
          }
          if (row.kind === "deliverable") {
            return `/projects/${projectId}/drawings/${row.slot.id}`;
          }
          // Neither is a place: *Unplaced* is a bucket, and the sentinel is a row about the
          // fetch rather than about the project.
          if (row.kind === "unplaced" || row.kind === "more") return null;
          if (row.synthetic === true) return null;
          return `/projects/${projectId}/tasks?under=${encodeURIComponent(row.id)}`;
        },
      },
      {
        /**
         * **One column, two answers** — `KLQ-20260828-003`, merged on the owner's instruction
         * on 2026-08-28 after it shipped as two.
         *
         * The question *"who is on this row?"* is one question. A task answers with the people
         * doing it; a level answers with the person responsible for it. Two columns put one
         * question in two places on one screen, and on any given row exactly one of them was
         * ever filled — a register with a permanently half-empty column teaches people to stop
         * reading it.
         *
         * So the column is constant and the *shape of the answer* is per row: `kind` is a
         * function, the way `applies`, `current` and `parse` already were.
         */
        key: "who",
        header: t("plan.column.who"),
        width: "22%",
        mobile: "meta",
        cell: (row) =>
          row.kind === "container"
            ? // A level's own responsible, or the one it inherits (§C.1). Editing it writes
              // the level; what is drawn may have come from an ancestor, and says so.
              inheritedCell(row.responsible)
            : row.kind === "task" ? (
                row.task.assignee_user_ids.length === 0 ? (
                  // Nobody is doing it yet, so the cell answers the next-best question
                  // instead: who owns the part of the project it sits in (§E). Drawn quieter
                  // and saying where it came from, because a person must never have to guess
                  // whether a name on a row was chosen for that row.
                  inheritedCell(row.responsible)
                ) : (
                  <span className={styles.rowMeta}>
                    {renderPersonNames(
                      t,
                      nameOf,
                      row.task.assignee_user_ids,
                      t("row.unassigned"),
                    )}
                  </span>
                )
              ) : row.kind === "deliverable" ? (
                <span className={styles.rowMeta}>
                  {row.slot.title ?? row.slot.name}
                </span>
              ) : null,
        value: (row) =>
          row.kind === "container"
            ? personLabel(row.responsible)
            : row.kind === "task"
              ? row.task.assignee_user_ids.length === 0
                ? personLabel(row.responsible)
                : renderPersonNames(
                    t,
                    nameOf,
                    row.task.assignee_user_ids,
                    t("row.unassigned"),
                  )
              : row.kind === "deliverable"
                ? (row.slot.title ?? row.slot.name)
                : "",
        edit: {
          // A task is owed by however many people the architect names; a level has one
          // responsible, because responsibility that splits four ways is not an answer to the
          // question it asks. Same column, two controls.
          kind: (row) => (row.kind === "container" ? "select" : "multiselect"),
          options: () => memberOptions,
          // A deliverable is an outcome, not work and not a place; a mixed selection is
          // filtered by this, never refused because of it (`KLQ-20260828-001` §C.1).
          applies: (row) => row.kind === "task" || row.kind === "container",
          /**
           * The second number the editor states — `KLQ-20260828-003` §F.2.
           *
           * This is the one column in the product whose write reaches rows it does not
           * touch: a level's responsible is inherited by everything beneath it that names
           * nobody (§C). *"3 rows"* is therefore the right number for the write and the wrong
           * one for the effect, and the editor says both. Only levels have reach — a task's
           * assignees are inherited by nothing, which is why the set is filtered to
           * containers rather than taken whole.
           */
          reach: (rows) =>
            inheritedBeneath(
              drawn,
              new Set(
                rows.flatMap((row) => (row.kind === "container" ? [row.id] : [])),
              ),
            ),
          // **The row's own value, never the resolved one.** Seeding the editor from an
          // inherited name would offer to write a value the person was only reading, and the
          // first Enter would turn every inherited level into a stated one.
          current: (row) =>
            row.kind === "container"
              ? row.responsibleUserId
              : row.kind === "task"
                ? [...row.task.assignee_user_ids]
                : null,
          // Picking in the cell **replaces** — it is what the cell shows. Adding somebody to
          // each of forty tasks without disturbing who is already there is a different
          // sentence, and it stays with the context menu's bulk action (§C.4).
          //
          // Empty means *nobody* on a task and *inherit* on a level, and neither needs a third
          // state to say so (`KLQ-20260828-003` §C.5).
          parse: (input, row) =>
            row.kind === "container"
              ? { ok: true, value: input === "" ? null : input }
              : {
                  ok: true,
                  value: input === "" ? [] : input.split(",").filter(Boolean),
                },
          // The row as it will be, before the server has said so (§F.1).
          optimistic: (row, value) =>
            row.kind === "container"
              ? {
                  ...row,
                  responsibleUserId: (value as string | null) ?? null,
                  responsible:
                    value === null || value === ""
                      ? null
                      : { userId: value as string, from: null },
                }
              : row.kind === "task"
                ? {
                    ...row,
                    task: {
                      ...row.task,
                      assignee_user_ids: (value as string[] | null) ?? [],
                    },
                  }
                : row,
        },
      },
      {
        key: "when",
        header: t("plan.column.when"),
        width: "18%",
        mobile: "meta",
        cell: (row) =>
          row.kind === "task" ? (
            <span className={styles.rowMeta}>
              {row.task.due_at === null
                ? t("row.noDue")
                : t("row.due", { when: relativeTime(row.task.due_at, locale) })}
            </span>
          ) : row.kind === "deliverable" ? (
            <span className={styles.rowMeta}>
              {/* The version, as the tree states it everywhere else: a counter when there is
                  one, and the absence said in words when there is not. */}
              {row.slot.current === null
                ? t("deliverable.noVersion")
                : t("plan.version", { counter: row.slot.current.counter })}
            </span>
          ) : null,
        // ISO, deliberately, and the date alone: a sheet parses `2026-09-30` into a date cell
        // in every locale, while "o 3 dni" parses into nothing anywhere. The screen keeps its
        // relative wording; the clipboard does not inherit it.
        value: (row) =>
          row.kind === "task"
            ? (row.task.due_at?.slice(0, 10) ?? "")
            : row.kind === "deliverable"
              ? row.slot.current === null
                ? ""
                : String(row.slot.current.counter)
              : "",
        edit: {
          kind: "date",
          applies: (row) => row.kind === "task",
          current: (row) => (row.kind === "task" ? row.task.due_at : null),
          // An empty box clears the date; anything else is a calendar day, sent as the instant
          // it starts in UTC. The same `parse` will take `2026-09-30` out of a pasted sheet.
          parse: (input) => {
            if (input.trim() === "") return { ok: true, value: null };
            const day = new Date(`${input}T00:00:00Z`);
            return Number.isNaN(day.getTime())
              ? { ok: false, reason: "date" }
              : { ok: true, value: day.toISOString() };
          },
          optimistic: (row, value) =>
            row.kind === "task"
              ? {
                  ...row,
                  task: { ...row.task, due_at: value as string | null },
                }
              : row,
        },
      },
      {
        key: "state",
        header: t("plan.column.state"),
        width: "12%",
        mobile: "meta",
        cell: (row) =>
          row.kind === "task" ? (
            <Pill tone={STATUS_TONE[row.task.status]}>
              {t(STATUS_KEY[row.task.status])}
            </Pill>
          ) : row.kind === "deliverable" ? (
            <Pill tone={DELIVERABLE_TONE[row.slot.status]}>
              {t(DELIVERABLE_KEY[row.slot.status])}
            </Pill>
          ) : null,
        value: (row) =>
          row.kind === "task"
            ? t(STATUS_KEY[row.task.status])
            : row.kind === "deliverable"
              ? t(DELIVERABLE_KEY[row.slot.status])
              : "",
        edit: {
          kind: "select",
          // Every status the workflow has. Which of them this task can actually reach is the
          // server's answer, per task — a selection of nine open and three done tasks moving
          // to `done` legitimately applies to nine and reports three, and a picker that hid
          // the option would be guessing on the client about a graph the server owns.
          options: () =>
            TASK_STATUSES.map((status) => ({
              id: status,
              label: t(STATUS_KEY[status]),
            })),
          applies: (row) => row.kind === "task",
          current: (row) => (row.kind === "task" ? row.task.status : null),
          // A task always has a status — there is no empty one. Refusing the empty
          // string here is what tells every writer so: a blank cell in a pasted sheet
          // leaves the column alone, and `Delete` over a selection skips it instead of
          // writing a status nothing renders (`TreeTable.tsx::clearCells`). It also closes
          // the picker's blank row, which until now set an optimistic `status: ""` that the
          // server then declined to write.
          parse: (input) =>
            input === ""
              ? { ok: false, reason: "required" }
              : { ok: true, value: input },
          optimistic: (row, value) =>
            row.kind === "task"
              ? { ...row, task: { ...row.task, status: value as TaskStatus } }
              : row,
        },
      },
    ];
    // The two helpers above are declared *inside* the memo rather than beside it: they close
    // over `t` and `nameOf`, which are already dependencies, so hoisting them would add two
    // identities that change every render and say nothing.
    // `drawn` is here for the `who` column's `reach` alone (§F.2): the second number is a
    // property of the tree beneath the edited rows, so it cannot be computed from the reached
    // rows on their own. It costs nothing — the table re-renders on a plan change anyway.
  }, [t, locale, nameOf, projectId, hasMore, memberOptions, drawn, sentinelFor]);

  /**
   * The one function that writes a cell edit — `KLQ-20260828-001` §C.3, §E.
   *
   * Every group is one `bulk-edit` request: N task ids, one field, one value. A fan-out is one
   * group; an undo is one group per distinct previous value, which is what makes twelve rows
   * come back to twelve different assignees in two or three requests rather than twelve.
   *
   * The mechanism above knows nothing about tasks and this function knows nothing about
   * selections — it is handed row ids and a value, and it maps the column key onto the
   * endpoint's field. Rows the server refuses come back per row, which is what the grid marks;
   * one refusal never takes the others down (§E.2).
   */
  const write = async (batch: EditBatch): Promise<EditResult> => {
    const applied: string[] = [];
    const failed: { rowId: string; reason: string }[] = [];

    /**
     * **Split by what the row *is*, not by which column it was** (`KLQ-20260828-003`, merged
     * into one column on 2026-08-28). `Kto` reaches tasks and levels alike, and the two are
     * written through different endpoints: a task through `bulk-edit`, a level through
     * `PATCH /hierarchy/{node_id}`, which is the only route the tree has.
     *
     * The split is per row and not per batch, because one gesture legitimately spans both —
     * dragging down a column across a floor and the work under it is the ordinary case, not the
     * exotic one. Per-row failure stays per-row (§E.2): a row the server refuses keeps its
     * previous value and says why, while the ones that succeeded keep the new one.
     */
    let wroteLevels = false;

    for (const group of batch.groups) {
      const levelIds = group.rowIds.filter((id) => containerIds.has(id));
      // A task row's id *is* its task id (`plan.ts`), and the deliverable rows underneath a
      // task are drawn from slots and have ids of their own — `applies` keeps those out.
      // Pivoted, a row id is `bucket::task` — the same task drawn under two objects is two
      // rows and must stay two rows for the selection to work at all. `leafIdOf` is the one
      // place that knows the shape (`KLQ-20260904-004` §B), and it is total, so this line is
      // unchanged in behaviour when nothing is pivoted.
      const taskIds = [
        ...new Set(
          group.rowIds
            .filter((id) => !containerIds.has(id))
            .map((id) => leafIdOf(id)),
        ),
      ];

      // One request per node: the tree has no bulk route, and a fan-out over levels is a
      // handful of rows. A register page is hundreds, which is why *that* one is batched.
      for (const nodeId of levelIds) {
        wroteLevels = true;
        try {
          await setNodeResponsible(
            projectId,
            nodeId,
            (group.value as string | null) ?? null,
          );
          applied.push(nodeId);
        } catch {
          failed.push({ rowId: nodeId, reason: t("error.unexpected") });
        }
      }

      if (taskIds.length === 0) continue;
      const body = bulkBodyFor(batch.colKey, group.value, taskIds, assigneesOf);
      if (body === null) continue;

      const answer = await bulkEdit.mutateAsync(body);
      applied.push(...answer.applied);
      for (const skip of answer.skipped) {
        failed.push({ rowId: skip.task_id, reason: t(bulkSkipKey(skip.code)) });
      }
    }

    // The tree is what changed, so the tree is what has to be refetched — the task pages are
    // untouched by a level write, which is `KLQ-20260828-003` §D.1 visible in the cache.
    if (wroteLevels) {
      await queryClient.invalidateQueries({
        queryKey: hierarchyQueryOptions(projectId).queryKey,
      });
    }

    return { applied, failed };
  };

  const navigate = useNavigate();
  // One fetch, shared with the filter bar and the detail panel: a label list is reference data
  // and does not change while a register is open.
  const projectLabels = useProjectLabels(projectId);
  const taskById = useMemo(
    () => new Map(tasks.map((one) => [one.id, one])),
    [tasks],
  );

  /**
   * The two beats a bulk action has, both held here because both are about the *subject*, and
   * the subject is this screen's selection: what is about to happen (asked above one task,
   * §H.3) and what did happen. They land in the action bar rather than in a dialog and a
   * toast, for the reason `components/SelectionBar.tsx` gives at length.
   */
  const [pending, setPending] = useState<{
    readonly intent: TaskBulkIntent;
    readonly taskIds: readonly string[];
  } | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  /** The rows a gesture named, as the tasks they mean (§D). */
  const subjectOf = (
    rowIds: readonly string[],
  ): { readonly tasks: readonly Task[]; readonly fromContainer: boolean } => {
    const subject = planSubjects(drawn, rowIds);
    return {
      // A task id the page no longer holds contributes nothing: the register is paginated, and
      // acting on a row that has scrolled out of the loaded set would be acting on something
      // nobody can see (§B.4, and `KLQ-20260821-001` §D).
      tasks: subject.taskIds
        .map((id) => taskById.get(id))
        .filter((one): one is Task => one !== undefined),
      fromContainer: subject.fromContainer,
    };
  };

  const runIntent = async (
    intent: TaskBulkIntent,
    taskIds: readonly string[],
  ): Promise<void> => {
    setPending(null);
    if (taskIds.length === 0) return;
    try {
      const answer = await bulkEdit.mutateAsync({
        task_ids: [...taskIds],
        ...intent.patch,
      });
      const first = answer.skipped[0];
      setOutcome(
        first === undefined
          ? t("bulk.applied", { count: answer.applied.length })
          : // Both halves, always: "3 failed" hides that nine worked, and the reason is the
            // server's own code rendered in this person's language (§E.3).
            t("bulk.appliedSkipped", {
              count: answer.applied.length,
              skipped: answer.skipped.length,
              reason: t(bulkSkipKey(first.code)),
            }),
      );
    } catch (error) {
      setOutcome(detailOf(error) ?? t(taskErrorKey(error)));
    }
  };

  /**
   * The menu, for the rows a gesture named — `KLQ-20260825-006` §C, §G.
   *
   * The rows arrive as an argument rather than being read out of the selection, because a
   * right-click outside the selection moves it and React has not applied that move yet; the
   * table resolves *which rows this click means* and hands them here.
   */
  /**
   * The table's own copy and paste, published by the primitive (`registerClipboard`).
   *
   * Held here because **both** menus a selection has are built from one list — the right-click
   * the table opens and the action bar this view draws — and only the first is the table's own
   * (`KLQ-20260825-006` §C: one item model, drawn everywhere it applies). Kept in state rather
   * than read out of a ref, because the two items change when the selection stops being empty
   * and the menus have to be re-rendered when they do.
   */
  const [clipboardItems, setClipboardItems] = useState<readonly MenuItem[]>([]);

  const menuItems = (rowIds: readonly string[]): readonly MenuItem[] => {
    const subject = subjectOf(rowIds);
    const actions = taskMenuItems({
      tasks: subject.tasks,
      members: memberOptions,
      labels: projectLabels.data?.items ?? [],
      t,
      onIntent: (intent) => {
        setOutcome(null);
        const taskIds = subject.tasks.map((one) => one.id);
        // One task is an act; more than one is a question first (§H.3). The threshold is one
        // rather than the cell editor's twenty-five because a menu item is a single click with
        // no value typed into it — there is nothing on the way that would make somebody notice
        // the count.
        if (taskIds.length > 1) setPending({ intent, taskIds });
        else void runIntent(intent, taskIds);
      },
      onOpen: (taskId) => {
        void navigate({
          to: "/projects/$projectId/tasks/$taskId",
          params: { projectId, taskId },
        });
      },
    });
    // At the end, under a rule: the domain's actions are what somebody came to the menu for,
    // and the grid's two are the general-purpose pair every table has. `drawnItems` prunes the
    // separator on its own when both clipboard items are hidden, so an empty selection does not
    // get a menu that ends in a line.
    return [
      ...actions,
      { id: "grid-sep", separator: true },
      ...clipboardItems,
    ];
  };

  const failure =
    list.error ?? hierarchy.error ?? slots.error ?? currents.error;

  return (
    // Selection is opt-in per surface (`KLQ-20260825-006` §B.6) and this is the first surface
    // to opt in. It wraps the whole view rather than the table alone, because the selection
    // action bar and the context menu land beside the table, not inside it, and both read the
    // same provider.
    <SelectionProvider>
      <EditingProvider write={write}>
        <div className={styles.plan}>
          {/* The filter bar is `TasksPage`'s, in `Screen`'s `toolbar` slot
              (`KLQ-20260910-001` §A) — one bar above all three arrangements. */}

          {/* The slice, its chips and the way out — the same bar the register draws, because
              the plan is the same rows under a different arrangement and a person switching
              between them must not lose sight of what they narrowed to
              (`KLQ-20260820-005` §2). */}
          <SliceBar
            under={filters.under}
            nameOf={nodeNameOf}
            excluded={excludedBySlice}
            onChange={(under) => {
              apply({ ...filters, under: [...under] });
            }}
          />

          <div className={styles.planMain}>
            <div className={styles.registerHead}>
              {list.isSuccess ? (
                <LoadedCount count={tasks.length} hasMore={hasMore} />
              ) : (
                <span />
              )}
              <div className={styles.headActions}>
                {/* The editing stack, visible. `Cmd+Z` in the grid does the same; this is for
                the person who just pressed `Delete` and wants the way back to be a thing they
                can see (owner, 2026-08-28). Inside the `EditingProvider`, so it reads the
                same stack the cells write to. */}
                <UndoButtons />
                {/* **Grouped by what, and whether** (`KLQ-20260904-004` §D, one control since
                    `KLQ-20260910-001` §E). The first option is the flat arrangement — not a
                    grouping called *none*, but the answer *do not group*, which is what the
                    separate `ViewToggle` beside this used to say. Two controls for one
                    question is what went; the object and phase entries still appear only when
                    the project has those vocabularies at all.

                    Not a URL parameter either way: the arrangement of a list is not part of
                    the question it answers, so it does not travel in a shared link. */}
                <Select
                  label={t("axis.legend")}
                  size="sm"
                  value={axis ?? ""}
                  onChange={(event) => {
                    const picked = event.target.value;
                    setAxis(picked === "" ? null : (picked as PlanAxis));
                  }}
                >
                  <option value="">{t("plan.mode.flat")}</option>
                  <option value="location">{t("axis.location")}</option>
                  {(objects.data ?? []).length > 0 ? (
                    <option value="object">{t("axis.object")}</option>
                  ) : null}
                  {(phases.data ?? []).length > 0 ? (
                    <option value="phase">{t("axis.phase")}</option>
                  ) : null}
                  <option value="status">{t("axis.status")}</option>
                  <option value="priority">{t("axis.priority")}</option>
                </Select>
                {/* UX cleanup 2026-08-25 — one Create for the screen, in the header.
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setCreating(true);
              }}
            >
              {t("create.action")}
            </Button>
            */}
              </div>
            </div>

            {failure != null ? (
              <InlineError variant="block">
                {detailOf(failure) ?? t(taskErrorKey(failure))}
              </InlineError>
            ) : list.isPending || tree === null ? (
              // The table's own geometry, so the rows that land do not move (`rowHeight` is
              // `--tree-row-height`). The first bar is the tick column, the second the name —
              // narrowed by the twisty and the root indent the real first cell carries — and
              // the rest follow the `22% / 18% / 12%` columns declared below.
              <SkeletonRows
                label={t("status.loading")}
                rows={5}
                rowHeight={36}
                columns={[24, "30%", "16%", "13%", "9%"]}
              />
            ) : (
              <TreeTable
                label={t("plan.table")}
                columns={columns}
                nodes={drawn}
                mode={mode}
                // **The fetch trigger.** A branch's tasks arrive when the branch is opened, so the
                // table reporting which ones are open is what makes the request happen at all.
                onOpenChange={setOpenLevels}
                rowLabel={(row) => rowName(row, t)}
                /* A container or an unplaced bucket holds work; it is not work. Pointing at one
               is what the twisty is for, and `KLQ-20260825-006` §D.1 maps a selected
               container to its loaded descendants at the *action* layer rather than pretending
               the row itself is a subject. */
                /* **A level is ticked like anything else** (owner, 2026-08-28). What that
                   *means* is still `KLQ-20260825-006` §D.1's answer and not a new one: a ticked
                   floor stands for the work loaded beneath it, which `planSubjects` has always
                   resolved and the action bar has always said out loud ("z načítaných").
                   Ticking it does not make the floor a task.

                   `unplaced` stays out. It is a bucket the view invented, with no row behind it
                   anywhere — there is nothing to tick and nothing to edit. `focusable` is left
                   to its default, which is this predicate: on this surface the two questions
                   happen to have the same answer, and passing the same function twice would be
                   two places to change it. */
                selectable={(row) =>
                  row.kind !== "unplaced" &&
                  !(row.kind === "container" && row.synthetic === true)
                }
                menuItems={menuItems}
                menuLabel={t("bulk.menu")}
                registerClipboard={setClipboardItems}
                /* Nothing selected + `Cmd+V` opens the import preview. Withheld until the tree
                   and the member list have arrived, because an adapter that cannot resolve a
                   location would report every row as an unknown place — which reads as the
                   sheet being wrong rather than as the screen still loading. */
                importAdapter={importAdapter.ready ? importAdapter : undefined}
                importSurface="tasks.hierarchy"
                onImported={refetchTasks}
                empty={
                  isNarrowed(filters) ? (
                    <EmptyState
                      variant="list"
                      explains={t("empty.explains")}
                      title={t("empty.filtered.title")}
                      body={t("empty.filtered.body")}
                      action={
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            apply(NO_FILTERS);
                          }}
                        >
                          {t("filter.clear")}
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      variant="list"
                      // TODO(-014): placeholder sentence in the brief's voice; wave 3 writes the real one.
                      explains={t("plan.explains")}
                      title={t("plan.emptyTitle")}
                      body={t("plan.empty")}
                    />
                  )
                }
              />
            )}

            {list.hasNextPage ? (
              <div ref={setSentinel} className={styles.sentinel}>
                <Button
                  size="sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => {
                    void list.fetchNextPage();
                  }}
                >
                  {list.isFetchingNextPage
                    ? t("list.loadingMore")
                    : t("list.more")}
                </Button>
              </div>
            ) : null}

            {/* Rule 3, said to the person and not only to the next reader of this file: a
              group here is as complete as the pages that have been loaded. The second half of
              that sentence — that the cap was a rolling window rather than a stop — went with
              the cap (`KLQ-20260910-001` §F): every page is kept now, so the honest statement
              is only that there are more to come. */}
            {partial ? <p className={styles.muted}>{t("plan.partial")}</p> : null}
          </div>

          {/* The second door (§G). Same list, other renderer — and the only one that exists on
              a touch screen or for somebody who was never told about right-click. */}
          <PlanActionBar
            t={t}
            build={menuItems}
            subjectOf={subjectOf}
            pending={pending}
            outcome={outcome}
            onApply={() => {
              if (pending !== null) void runIntent(pending.intent, pending.taskIds);
            }}
            onCancel={() => {
              setPending(null);
            }}
          />

          {/* UX cleanup 2026-08-25 — hoisted to `TasksPage`, wiring unchanged.
      {creating ? (
        <TaskCreateDrawer
          projectId={projectId}
          locationId={filters.under[0] ?? null}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
      */}
        </div>
      </EditingProvider>
    </SelectionProvider>
  );
}

/**
 * One column's edit, as a `bulk-edit` body — the only place in this file that knows the
 * endpoint's field names.
 *
 * Assignees are a **delta** on the wire (§E.2), so "make these the same" is expressed as
 * *remove what is there, add what was chosen*. That is `KLQ-20260828-001` §C.4: typing in the
 * cell replaces, because it is what the cell shows; the context menu keeps add and remove,
 * because that is a different sentence.
 */
function bulkBodyFor(
  colKey: string,
  value: unknown,
  taskIds: readonly string[],
  assigneesOf: ReadonlyMap<string, readonly string[]>,
): TaskBulkEdit | null {
  if (colKey === "state") {
    return typeof value === "string" && value !== ""
      ? {
          task_ids: [...taskIds],
          clear_due_at: false,
          status: value as TaskStatus,
        }
      : null;
  }
  if (colKey === "when") {
    return value === null
      ? { task_ids: [...taskIds], clear_due_at: true }
      : typeof value === "string"
        ? { task_ids: [...taskIds], clear_due_at: false, due_at: value }
        : null;
  }
  if (colKey === "who") {
    const chosen = Array.isArray(value) ? (value as string[]) : [];
    // Every id currently held by any task in this group leaves; the chosen one arrives. The
    // server applies the removal per task, so naming somebody a task never had is a no-op
    // rather than an error.
    const leaving = new Set<string>();
    for (const taskId of taskIds) {
      for (const userId of assigneesOf.get(taskId) ?? []) leaving.add(userId);
    }
    for (const userId of chosen) leaving.delete(userId);
    return {
      task_ids: [...taskIds],
      clear_due_at: false,
      assignees: { add: chosen, remove: [...leaving] },
    };
  }
  return null;
}

/**
 * The action bar, mounted under the selection provider.
 *
 * It is a component of its own for one reason only: `useSelection` cannot be called by the
 * same component that mounts `<SelectionProvider>`. Everything it renders — the items, the
 * sentence, the question — is computed above and handed in, so this is genuinely only the
 * subscriber, and the screen's logic does not fork into two places that both know what a
 * selection means.
 */
function PlanActionBar({
  t,
  build,
  subjectOf,
  pending,
  outcome,
  onApply,
  onCancel,
}: {
  readonly t: Translate<"tasks">;
  readonly build: (rowIds: readonly string[]) => readonly MenuItem[];
  readonly subjectOf: (rowIds: readonly string[]) => {
    readonly tasks: readonly Task[];
    readonly fromContainer: boolean;
  };
  readonly pending: {
    readonly intent: TaskBulkIntent;
    readonly taskIds: readonly string[];
  } | null;
  readonly outcome: string | null;
  readonly onApply: () => void;
  readonly onCancel: () => void;
}) {
  const selection = useSelection();
  const rows = selection.rows;
  if (rows.length === 0) return null;

  const subject = subjectOf(rows);
  // A selection of pure structure — a drawing with no owning task, an empty floor — is a real
  // selection and not an error, but there is nothing to offer it (§D.4). The bar stays away
  // rather than appearing with an empty row of buttons.
  if (subject.tasks.length === 0) return null;

  return (
    <SelectionBar
      // §D.2: the count is the *task* count, never the row count — selecting one floor and
      // being told "1 selected" would be a lie about what the next click does. And §D.3: when
      // a container contributed, the sentence says *loaded*.
      summary={t(
        subject.fromContainer ? "bulk.selectedLoaded" : "bulk.selected",
        { count: subject.tasks.length },
      )}
      items={build(rows)}
      onClear={selection.clear}
      clearLabel={t("bulk.clear")}
      moreLabel={t("bulk.more")}
      status={outcome}
      confirm={
        pending === null
          ? null
          : {
              question: t("bulk.confirm", {
                what: pending.intent.what,
                count: pending.taskIds.length,
              }),
              apply: t("bulk.apply"),
              cancel: t("bulk.cancel"),
              onApply,
              onCancel,
            }
      }
    />
  );
}

/** A row's name, for the twisty's accessible label. Never rendered as visible text. */
function rowName(row: PlanRow, t: Translate<"tasks">): string {
  switch (row.kind) {
    case "container":
      return row.name;
    case "unplaced":
      return t("plan.unplaced");
    case "task":
      return row.task.title;
    case "deliverable":
      return row.slot.identifier;
    case "more":
      return t("plan.more");
  }
}

/**
 * The first column: what the row *is*.
 *
 * A task's title is the link, as in the register — C8 is where a task is read and argued
 * about. A deliverable's identifier is the link, to C1a's viewer, and it is printed verbatim
 * off the wire because the identifier grammar belongs to the tree and never to the work
 * (`naming.render` does not read `owning_task_id`, and this side must not imply that it does).
 * The drawing number, when the slot carries one, is an extra beside it and never a
 * replacement — the identifier is what the register, the transmittal and C8 all quote.
 */
function NameCell({
  projectId,
  row,
  t,
  hasMore,
  sentinelFor,
}: {
  readonly projectId: string;
  readonly row: PlanRow;
  readonly t: Translate<"tasks">;
  /** The list's own cursor state — §D's `+` on a group count, for the reason rule 3 gives. */
  readonly hasMore: boolean;
  /**
   * Where a branch's sentinel registers itself (`lib/infinite.ts`). Called with the level's id
   * and handed back a ref callback, so the element that asks for the next page is the row that
   * marks where it would go.
   */
  readonly sentinelFor: (nodeId: string) => (element: HTMLElement | null) => void;
}) {
  switch (row.kind) {
    case "container":
      return (
        <span className={styles.planGroup}>
          {row.name}
          <LoadedCount count={row.taskCount} hasMore={hasMore} bare />
        </span>
      );
    case "unplaced":
      return (
        <span className={styles.planGroup}>
          {t("plan.unplaced")}
          <LoadedCount count={row.taskCount} hasMore={hasMore} bare />
        </span>
      );
    // The branch's own bottom edge. `aria-live` because it is a status somebody scrolled into
    // rather than a control they pressed — a screen reader should hear that more is coming, and
    // then hear the rows. `role="status"` would be a second announcement of the same thing.
    case "more":
      return (
        <span
          ref={row.remaining === null ? undefined : sentinelFor(row.nodeId)}
          className={styles.planMore}
          aria-live="polite"
        >
          {t("plan.more")}
          {row.remaining === null || row.remaining <= 0 ? null : (
            <span className={styles.muted}>
              {t("plan.moreCount", { count: row.remaining })}
            </span>
          )}
        </span>
      );
    case "task":
      return (
        <span className={styles.planName}>
          <KindGlyph
            kind={row.task.kind}
            label={t(KIND_KEY[row.task.kind])}
            taskTypeId={row.task.task_type_id}
          />
          <Link
            to="/projects/$projectId/tasks/$taskId"
            params={{ projectId, taskId: row.task.id }}
            className={styles.rowLink}
          >
            <span className={styles.number}>
              {t("row.number", { number: row.task.number })}
            </span>{" "}
            {row.task.title}
          </Link>
        </span>
      );
    case "deliverable":
      return (
        <span className={styles.planName}>
          <Link
            to="/projects/$projectId/drawings/$slotId"
            params={{ projectId, slotId: row.slot.id }}
            search={{}}
            className={styles.planIdentifier}
          >
            {row.slot.identifier}
          </Link>
          {row.slot.drawingNumber === null ? null : (
            <span className={styles.rowMeta}>{row.slot.drawingNumber}</span>
          )}
        </span>
      );
  }
}
